import { wallDistance, wallLength, type PaintedWallGeometry } from '../../../shared/painted-wall-geometry';
import { paintedWallSection } from '../../../shared/painted-wall-sections';
import type { PaintedWallSectionState } from '../../../shared/painted-wall-section-state';
import { wallRunSizeIdForLength } from '../../inventory/wall-run-size';
import { objectVitalityMax } from '../../compile/object-vitality';
import { roundPhpDecimal } from '../../compile/php-decimal-values';
import { emptySpawnPlan, type SpawnRowPlan } from '../../spawn/row-plan';
import { definitionNotHeld, handBack, itemField, record, type Row } from '../context';

export const ICE_WALL = '98E982F2-46EF-1B81-87D2-6B0E8AFDE922';
export const ICE_WALL_RUN = '571AE600-2039-916E-3A9F-7E11FAB029C1';
export const ICE_MATERIAL = '9DEF31B5-8E21-2372-FE2A-95DB8DF43BFE';
export interface IceWallSections { sections: PaintedWallSectionState; rows: SpawnRowPlan }
export interface IceWallSectionReaders {
  definition(id: string): Row | null;
  value(row: Row | null, key: string): unknown;
  sizeScale(id: unknown): unknown;
  density(id: string): unknown;
  uuid(): string;
}

/** ft_painted_wall_ice_cast_sections/materialize_section: cast-local 5 ft intervals, no room-wall transforms. */
export function planIceWallSections(wall: PaintedWallGeometry, encounterId: string, read: IceWallSectionReaders): IceWallSections {
  const source = read.definition(ICE_WALL_RUN);
  if (!source) definitionNotHeld('ft_painted_wall_ice_cast_sections:definition-not-held', ICE_WALL_RUN);
  if (!read.definition(ICE_MATERIAL)) definitionNotHeld('ft_painted_wall_ice_cast_sections:material-not-held', ICE_MATERIAL);
  const density = Number(read.density(ICE_MATERIAL));
  if (!Number.isFinite(density) || density <= 0) handBack('ft_painted_wall_ice_cast_sections:density-not-held');
  const count = Math.max(1, Math.ceil((wallLength(wall.points) - 1e-6) / 5));
  if (!Number.isSafeInteger(count) || count > 4096) handBack('ft_painted_wall_ice_cast_sections:section-limit');
  const sections: PaintedWallSectionState = { schema: 'ft.wall-sections.v1', sectionFt: 5, revision: count, entries: [] };
  const rows = emptySpawnPlan();
  for (let index = 0; index < count; index++) {
    const part = paintedWallSection(wall, index, 5);
    if (!part) handBack('ft_painted_wall_ice_cast_sections:section-geometry');
    const length = part.endFt - part.startFt, size = wallRunSizeIdForLength(length);
    const scale = read.sizeScale(size);
    if (scale == null || !Number.isFinite(Number(scale)) || Number(scale) <= 0) handBack('ft_painted_wall_ice_cast_sections:size-scale-not-held');
    const sized: Row = { items: source.items, item_layer_types: { ...record(source.item_layer_types), size, v_max: 0, v_max_override: 0 } };
    const maximum = objectVitalityMax(sized, { layerValue: read.value }, () => scale, read.definition);
    if (maximum === null) handBack('ft_painted_wall_ice_cast_sections:vitality-not-held');
    let remaining = length / 2, center = part.geometry.points[0]!;
    for (let i = 1; i < part.geometry.points.length; i++) {
      const a = part.geometry.points[i - 1]!, b = part.geometry.points[i]!, run = wallDistance(a, b);
      if (remaining <= run) {
        const t = remaining / run;
        center = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
        break;
      }
      remaining -= run;
    }
    const inventoryId = read.uuid();
    rows.writes.push({ table: 'encounter_inventory', op: 'insert', row: {
      inventory_id: inventoryId, inventory_item_id: ICE_WALL_RUN, inventory_name: String(itemField(source, 'item_name') ?? ''),
      possess_id: encounterId, item_qty: 1, equipped: '', inventory_deleted: 0, inventory_wares: 0,
      inventory_size: size, enchantment_template_id: null, v_max: maximum, v_current: maximum, v_temp: 0, v_max_temp: 0,
      x_pos: center.x, y_pos: center.y, z_pos: center.z,
      weight_override: roundPhpDecimal(length * wall.heightFt * wall.thicknessFt * density, 2),
    } });
    sections.entries.push({ index, inventoryId, phase: 'solid' });
  }
  return { sections, rows };
}
